/**
 * Запрос восстановления пароля.
 *
 * Ответ одинаков независимо от того, есть такой адрес или нет — `202` в обоих
 * случаях. Поэтому и экран после отправки один: «письмо отправлено, если такой
 * адрес у нас есть». Написать «такой почты нет» означало бы отдать список
 * клиентов любому, кто перебирает адреса.
 */

import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { AuthCard } from './AuthCard';
import { Field } from '../../ui/form';
import { ErrorNotice } from '../../ui/state';
import { forgotPassword } from '../../api/auth';
import { ApiError, fieldErrors } from '../../api/problem';

export function Forgot() {
  // Адрес приезжает из экрана регистрации: человек, чей адрес уже занят,
  // приходит сюда именно за паролем, и набирать почту заново незачем.
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [fields, setFields] = useState<Record<string, string>>({});

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFields({});
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (caught) {
      if (caught instanceof ApiError) setFields(fieldErrors(caught.problem));
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <AuthCard
        title="Проверьте почту"
        lead={
          <>
            Если {email} у нас есть, мы отправили на него ссылку для нового пароля. Ссылка
            действует сутки.
          </>
        }
        footer={
          <p className="micro">
            Письма нет? Загляните в спам или{' '}
            <button className="linkish" type="button" onClick={() => setSent(false)}>
              отправьте ещё раз
            </button>
          </p>
        }
      >
        <Link className="btn btn-ghost" to="/auth/login">
          Вернуться ко входу
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Восстановление пароля"
      lead="Укажите почту, которой вы входите. Пришлём ссылку, по которой можно задать новый пароль."
      footer={
        <p className="micro">
          <Link to="/auth/login">Вернуться ко входу</Link>
        </p>
      }
    >
      {error ? <ErrorNotice error={error} /> : null}

      <form onSubmit={submit} noValidate>
        <Field
          id="email"
          label="Почта"
          hint="Та, на которую оформлена подписка"
          error={fields.email}
        >
          {(props) => (
            <input
              {...props}
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>

        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? 'Отправляем…' : 'Прислать ссылку'}
        </button>
      </form>
    </AuthCard>
  );
}
